import React, { useCallback, useEffect, useState } from "react";
import {
  ConciergeStandalone,
  ConciergeWidgetState,
  PageContext,
  useAppBootstrapV2,
  useFingerprintJSContext,
  useIsDtcConciergeEnabled,
} from "@brandclub/common-ui";
import { useConciergeWidget } from "./useConciergeWidget";
import { useUserProfile } from "../../hooks/useUserProfile";
import {
  type CurrentProductContext,
  useCurrentProductContext,
} from "../../hooks/useCurrentProductContext";
import { APPID, HOST_DOMAIN } from "../../utils/domain";
import { queryClient } from "../../api/cache/queryClient";
import type { BcMountSource } from "../../index";
import { ConversationStartersPortal } from "./ConversationStartersPortal";
import { useConciergeCommerce } from "./conciergeCommerce";
import { queryOptions, useQuery } from "@tanstack/react-query";
import {
  type BrandclubProductByRetailerSkuResponse,
  getBrandclubProductByRetailerSku,
} from "../../api/rest/product";
import {
  getCurrentProductContextSource,
  getPagesShowConciergeFromMeta,
} from "../../utils/bcProductMetadataUtils";

// Extend Window interface for Shopify global
declare global {
  interface Window {
    Shopify?: {
      theme?: {
        name?: string;
        id?: number | string;
      };
    };
  }
}

// common-ui expects the origin only — it prepends /api-rewards internally
const API_BASE_URL = HOST_DOMAIN;

const conciergeRetailerProductQueryOptions = ({
  isCatalogV2,
  storeKind,
  retailerSku,
}: CurrentProductContext) => {
  return queryOptions({
    queryKey: ["concierge-retailer-sku-product", storeKind, retailerSku],
    queryFn: ({ signal }) => {
      if (!retailerSku) {
        throw new Error(
          "Failed to fetch concierge product context: missing retailerSku"
        );
      }
      return getBrandclubProductByRetailerSku(retailerSku, storeKind, signal);
    },
    enabled: isCatalogV2 && !!retailerSku && !!storeKind,
    notifyOnChangeProps: ["data"],
  });
};

const getEffectiveStacklineSku = ({
  retailerProduct,
  currentProductContext,
  activeStacklineSku,
}: {
  retailerProduct?: BrandclubProductByRetailerSkuResponse;
  currentProductContext: CurrentProductContext;
  activeStacklineSku?: string;
}): string | undefined => {
  return (
    retailerProduct?.stacklineSku ??
    currentProductContext.stacklineSku ??
    activeStacklineSku ??
    undefined
  );
};

export interface ConciergeButtonProps {
  /**
   * How this copy of the bundle was started. Everything below is decided from
   * THIS value, never from store-wide config — see `BcMountSource`.
   *
   * - `theme-script`     a merchant's own pasted `<script>`. Unmounts entirely
   *                      when the Split rollout is off (long-standing behaviour).
   * - `app-proxy`        our Shopify app embed. The loader has already applied
   *                      the store's Off/Preview/Live switch and tells us the
   *                      answer in `showConcierge`.
   * - `pages-storefront` the canonical Pages storefront. Stays mounted so an
   *                      EXPLICIT open (`askConcierge()`) can render the pane.
   *                      Proactive UI gates on the Split unless the page store
   *                      or mount opts set `showConcierge` to false.
   */
  mountSource?: BcMountSource;
  /**
   * May proactive UI (floating button + starters) show?
   * `app-proxy`: omitted ⇒ true. `pages-storefront`: false at mount or on
   * `window.bcProductMetaData.showConcierge` hides proactive UI; omitted ⇒ Split.
   */
  showConcierge?: boolean;
  /**
   * Whether this page load mounted the Brandclub cart (the bag drawer and its
   * writer). The concierge may fill the Brandclub bag only when it is mounted.
   * Omitted ⇒ true, the same default `CheckoutShopApp` uses.
   */
  cartEnabled?: boolean;
}

/**
 * Wrapper component that sets up ConciergeStandalone with checkout-shop-app specific configuration
 */
export const ConciergeButton: React.FC<ConciergeButtonProps> = ({
  mountSource = 'theme-script',
  showConcierge,
  cartEnabled = true,
}) => {
  const {
    isOpen,
    open,
    close,
    toggle,
    isMaximized,
    setMaximized,
    pendingQuery,
    setPendingQuery,
    activeStacklineSku,
  } = useConciergeWidget();
  const { data: userProfile } = useUserProfile();
  const visitorTracking = useFingerprintJSContext();
  const bcVisitorId = visitorTracking?.uniqueVisitorId ?? undefined;
  const currentProductContext = useCurrentProductContext();
  const [productContextSource, setProductContextSource] = useState<
    PageContext["source"]
  >(getCurrentProductContextSource);
  const [pagesShowConcierge, setPagesShowConcierge] = useState<
    boolean | undefined
  >(() =>
    mountSource === "pages-storefront"
      ? getPagesShowConciergeFromMeta()
      : undefined
  );
  const { data: appBootstrapV2 } = useAppBootstrapV2(API_BASE_URL);
  const brandAppV2AppId = appBootstrapV2?.app.appId;
  // Which bag "Add to bag" fills in the bubble. See `conciergeCommerce.ts` for the rule.
  const commerce = useConciergeCommerce({ bootstrap: appBootstrapV2, cartEnabled });
  const { data: currentRetailerProduct } = useQuery(
    conciergeRetailerProductQueryOptions(currentProductContext)
  );

  // Create widget state object matching the ConciergeWidgetState interface
  const widgetState: ConciergeWidgetState = {
    isOpen,
    open,
    close,
    toggle,
    isMaximized,
    setMaximized,
  };

  useEffect(() => {
    if (bcVisitorId) {
      // eslint-disable-next-line no-console
      console.info("[ShopperOS Concierge] BC visitorId", bcVisitorId);
    }
  }, [bcVisitorId]);

  // On the Shopify App Proxy surface the merchant's own actions ARE the opt-in:
  // they installed the app from the App Store and switched the theme embed on.
  // The `brandclub_dtc_concierge` Split targets `domain` with an exact "Is in
  // list" match, so a self-serve merchant's storefront can never be in it —
  // shipping the bubble behind that gate would mean every real merchant sees
  // nothing, with no error anywhere (Plan 10 S5).
  //
  // The kill switch does not disappear, it moves somewhere we own per store:
  // `servingSurface` on the hosting binding, flipped from ShopperOS (D-23).
  // Front-room brands are untouched and keep gating on the Split alone.
  //
  // SUPERSEDED 2026-09-01: the serving surface is a STORE-wide fact and was
  // being used to answer a PER-COPY question, so flipping it un-gated the
  // concierge on a legacy theme copy too. The kill switch now travels with the
  // mount (`mountSource` + `showConcierge`) — see the visibility block below.
  // `servingSurface` still decides WHERE the concierge lives (canonical URL,
  // auth), just not WHO can see it.

  const isConciergeEnabledViaSplit = useIsDtcConciergeEnabled({
    email: userProfile?.email,
    phoneNumber: userProfile?.phoneNumber,
    appId: APPID,
    domain: window.location.hostname,
    path: window.location.pathname,
    themeName: window.Shopify?.theme?.name,
    themeId: window.Shopify?.theme?.id?.toString(),
    visitorId: bcVisitorId,
  });

  // Which copy am I? Both answers below hang off this, and off nothing else.
  const isProxyCopy = mountSource === 'app-proxy';
  const keepMountedWhenConciergeOff = mountSource !== 'theme-script';

  /**
   * Proactive UI (floating button + conversation starters).
   *
   * This USED to read `isConciergeEnabledViaSplit || isAppProxySurface`, where
   * `isAppProxySurface` is store-wide config. So the moment a store's serving
   * surface was flipped to the App Proxy, the LEGACY theme copy — which knows
   * nothing about any of this — also read "yes" and showed the concierge to
   * every shopper, straight past the brand's Split rollout. That is exactly
   * what happened to PetArmor on 2026-09-01, an hour after adoption.
   *
   * Now: the App-Proxy copy is told the answer by the loader that owns the
   * merchant's Off/Preview/Live switch, and EVERY other copy falls back to the
   * Split — which is the only rollout mechanism those copies ever had.
   */
  const pagesProactiveOff =
    mountSource === "pages-storefront" &&
    (showConcierge === false || pagesShowConcierge === false);
  const isConciergeEnabled = pagesProactiveOff
    ? false
    : isProxyCopy
      ? showConcierge !== false
      : isConciergeEnabledViaSplit;

  const effectiveStacklineSku = getEffectiveStacklineSku({
    retailerProduct: currentRetailerProduct,
    currentProductContext,
    activeStacklineSku: activeStacklineSku ?? undefined,
  });
  const pageContext: PageContext | undefined = effectiveStacklineSku
    ? {
        pageType: "pdp",
        stacklineSku: effectiveStacklineSku,
        source: productContextSource,
      }
    : undefined;

  useEffect(() => {
    const updateFromPagesMeta = () => {
      setProductContextSource(getCurrentProductContextSource());
      if (mountSource === "pages-storefront") {
        setPagesShowConcierge(getPagesShowConciergeFromMeta());
      }
    };
    updateFromPagesMeta();
    window.addEventListener("bcProductMetaDataChanged", updateFromPagesMeta);
    const interval = window.setInterval(updateFromPagesMeta, 500);
    return () => {
      window.removeEventListener("bcProductMetaDataChanged", updateFromPagesMeta);
      window.clearInterval(interval);
    };
  }, [mountSource]);

  // Handlers for maximize/minimize
  const handleMaximize = useCallback(() => {
    setMaximized(true);
  }, [setMaximized]);

  const handleMinimize = useCallback(() => {
    setMaximized(false);
  }, [setMaximized]);

  // Handler for when a conversation starter question is clicked
  const handleQuestionClick = useCallback(
    (question: string, stacklineSku: string) => {
      setPendingQuery({ query: question, stacklineSku });
      open(); // Open the widget
    },
    [open, setPendingQuery]
  );

  // Handler for when the initial query has been processed
  const handleInitialQueryProcessed = useCallback(() => {
    setPendingQuery(null);
  }, [setPendingQuery]);

  // EXISTING behavior by default: when the Split rollout is off, unmount entirely.
  // CONTROLLED callers (Pages storefront) opt out of that unmount so an explicit
  // open can render the pane — proactive UI still self-gates on `isConciergeEnabled`
  // (floating button + conversation starters), so nothing proactive shows when off.
  if (!keepMountedWhenConciergeOff && !isConciergeEnabled) {
    return null;
  }

  return (
    <>
      <ConciergeStandalone
        widgetState={widgetState}
        pageContext={pageContext}
        showMaximize={true}
        isMaximized={isMaximized}
        onMaximize={handleMaximize}
        onMinimize={handleMinimize}
        isConciergeEnabled={isConciergeEnabled}
        conciergeControlled={keepMountedWhenConciergeOff}
        hideOnConciergePage={false}
        apiBaseUrl={API_BASE_URL}
        queryClient={queryClient}
        appId={appBootstrapV2?.app.appId}
        appName={appBootstrapV2?.app.appName}
        initialQuery={pendingQuery?.query}
        initialQueryStacklineSku={pendingQuery?.stacklineSku}
        onInitialQueryProcessed={handleInitialQueryProcessed}
        brandId={appBootstrapV2?.app.brandIds?.[0]}
        // Cross-surface link for the expand/history icons (Plan 6 / D-23):
        // the server-computed canonical URL for the brand's chosen serving
        // surface (e.g. `https://<shop>/a/sos/concierge` on Shopify), with
        // the pre-switch HOST_DOMAIN behavior as fallback until the backend
        // projects the field.
        conciergePageUrl={appBootstrapV2?.app.canonicalConciergeUrl ?? `${HOST_DOMAIN}/concierge`}
        commerce={commerce}
      />
      <ConversationStartersPortal
        isConciergeEnabled={isConciergeEnabled}
        onQuestionClick={handleQuestionClick}
        appName={appBootstrapV2?.app.appName}
        appId={brandAppV2AppId}
      />
    </>
  );
};
